import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'twin.macro';

import Button from 'components/button/Button';
import { Modal } from 'components/page/app-page/Modal';
import { Title3 } from 'components/typography/Typography';
import { usePendingSave } from 'context/PendingSaveContext';
import {
  CHANGES_WERE_PUBLISHED,
  COULD_NOT_PUBLISH,
  WORK_WAS_KEPT,
  WaitingChange,
  WaitingChanges,
  closedLabel,
  discardChanges,
  discardClosedChanges,
  getWaitingChanges,
  hourAndMinute,
  publishChanges,
  waitingLabel,
} from 'api/waitingChanges';
import { stopTheArrivingAnswer } from 'utils/aiAnswerInProgress';

/**
 * The sidebar's part of "keeping and publishing are two things" (D2), on a
 * campaign whose job is open (scenes 6, 7 and 9).
 *
 * Every text here is fixed by the description (section 5c) and must not be
 * invented: the label, "Publish changes (3)", "Discard changes", "Published at
 * 14:30", "Show me what will change?", the list and the discard question.
 *
 * So far it knows the JOB PAGE's changes only. The ad builder, targeting and
 * the interview still act at once on a running campaign, as they did before;
 * they join this count when they are built.
 */
type Step = 'idle' | 'ask' | 'list' | 'discard';

const HOW_OFTEN_TO_LOOK_MS = 30_000;
/** Scene 12's own words: the reason a publish or a discard is refused. */
export const NOT_SAVED_YET = 'Your last changes are not saved yet.';

const PublishChanges: React.FC<{ vacancyId: number }> = ({ vacancyId }) => {
  const [waiting, setWaiting] = useState<WaitingChanges | null>(null);
  const [step, setStep] = useState<Step>('idle');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const { keepNow, abandonWork } = usePendingSave();
  /*
   * Every answer about what is waiting carries a number; only the newest
   * asked-for one is shown. A look started before a publish and answered
   * after it would otherwise put "Publish changes (N)" back, and a look for
   * the campaign just left would put ITS list over this campaign's button
   * (review of slice 2).
   */
  const asked = useRef(0);
  const working = useRef(false);
  /** The step last shown: its content stays while the pop-up fades out. */
  const shownStep = useRef<typeof step>(step);
  if (step !== 'idle') shownStep.current = step;
  const shown = shownStep.current;

  const show = useCallback((ticket: number, answer: WaitingChanges) => {
    if (ticket === asked.current) setWaiting(answer);
  }, []);

  const look = useCallback(() => {
    if (working.current) return;
    asked.current += 1;
    const ticket = asked.current;
    getWaitingChanges(vacancyId)
      .then((answer) => show(ticket, answer))
      // Not knowing is not shown as "nothing waiting": the last answer stays.
      .catch(() => undefined);
  }, [vacancyId, show]);

  // On opening the campaign, whenever a screen says its work was kept, and
  // every half minute for what a colleague changed meanwhile (scene 8).
  useEffect(() => {
    setWaiting(null);
    setStep('idle');
    setProblem(null);
    look();
    const again = window.setInterval(look, HOW_OFTEN_TO_LOOK_MS);
    window.addEventListener(WORK_WAS_KEPT, look);
    return () => {
      asked.current += 1;
      window.clearInterval(again);
      window.removeEventListener(WORK_WAS_KEPT, look);
    };
  }, [look]);

  const byScreen = useMemo(() => {
    const groups = new Map<string, WaitingChange[]>();
    (waiting?.changes ?? []).forEach((change) => {
      groups.set(change.screen, [...(groups.get(change.screen) ?? []), change]);
    });
    return Array.from(groups.entries());
  }, [waiting]);

  const close = () => {
    setStep('idle');
    setProblem(null);
  };

  if (!waiting) return null;
  const closedCount = waiting.waits ? 0 : waiting.closed?.count ?? 0;
  if (!waiting.waits && closedCount === 0) return null;

  /*
   * 5b item 11: publish pressed while a save is pending waits for the save; if
   * the work cannot be saved the publish is REFUSED, in scene 12's words.
   * There is no way from here to "publish without them".
   */
  const publish = () => {
    if (working.current) return;
    working.current = true;
    setProblem(null);
    setBusy(true);
    asked.current += 1;
    const ticket = asked.current;
    keepNow()
      .then((allKept) => {
        if (!allKept) {
          setProblem(NOT_SAVED_YET);
          return undefined;
        }
        return publishChanges(vacancyId).then(
          (after) => {
            show(ticket, after);
            setStep('idle');
            window.dispatchEvent(new CustomEvent(CHANGES_WERE_PUBLISHED));
          },
          () => setProblem(COULD_NOT_PUBLISH)
        );
      })
      .finally(() => {
        working.current = false;
        setBusy(false);
        look();
      });
  };

  /*
   * Scene 9. Nothing is given up until the server has said the discard
   * happened (review of slice 2: given up first, a discard that failed left
   * the typed words on screen and out of the queue).
   *
   * So first whatever is still on its way is stored, and waited for: a save
   * landing AFTER the discard would bring every discarded word back as a
   * waiting change. If that cannot be stored the discard is refused too. Then
   * the discard; only then is anything typed meanwhile thrown away and the
   * page loaded afresh, so every screen shows what is public and the undo
   * steps are gone with it (5b item 19).
   */
  const discard = () => {
    if (working.current) return;
    working.current = true;
    setProblem(null);
    setBusy(true);
    stopTheArrivingAnswer();
    keepNow()
      .then((allKept) => {
        if (!allKept) {
          setProblem(NOT_SAVED_YET);
          return undefined;
        }
        return (
          closedCount > 0
            ? discardClosedChanges(vacancyId)
            : discardChanges(vacancyId)
        ).then(
          () => {
            abandonWork();
            window.location.reload();
          },
          () => setProblem('Could not discard, nothing was changed. Try again.')
        );
      })
      .finally(() => {
        working.current = false;
        setBusy(false);
        look();
      });
  };

  /*
   * A CLOSED campaign: nothing is public, so there is nothing to publish
   * here. What the ad builder and the application form kept is applied at
   * re-opening; the sidebar says how much that is and lets the person drop
   * it (Steven, 5 Oct 2026, an exception to D42).
   */
  if (!waiting.waits) {
    return (
      <div
        tw="px-4 flex flex-col items-center"
        data-testid="closed-changes-area"
      >
        <p
          tw="text-xs text-gray-500 mt-3 text-center"
          data-testid="closed-label"
        >
          {closedLabel(closedCount)}
        </p>
        <button
          type="button"
          tw="text-xs text-gray-500 font-medium mt-2 underline"
          data-testid="discard-closed-changes"
          disabled={busy}
          onClick={() => setStep('discard')}
        >
          Discard changes
        </button>
        <Modal show={step === 'discard'} onClose={close}>
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="discard-closed-question"
          >
            <Title3>Discard changes?</Title3>
            <p tw="text-sm text-gray-700">
              This drops what was changed while the campaign was closed. It
              cannot be undone.
            </p>
            {problem && (
              <p tw="text-sm text-red-600" data-testid="publish-problem">
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="discard-cancel"
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                testId="discard-confirm"
                disabled={busy}
                onClick={discard}
              >
                Discard
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  const nothingWaiting = waiting.count === 0;

  return (
    <div
      tw="px-4 flex flex-col items-center"
      data-testid="publish-changes-area"
    >
      {!nothingWaiting && (
        <p
          tw="text-xs text-gray-500 mt-3 text-center"
          data-testid="waiting-label"
        >
          {waitingLabel(waiting)}
        </p>
      )}
      <div tw="w-full pt-3">
        <Button
          id="menu-campaign-publish-changes"
          testId="publish-changes"
          disabled={nothingWaiting || busy}
          onClick={() => setStep('ask')}
        >
          {nothingWaiting
            ? `Published at ${hourAndMinute(waiting.published_at)}`
            : `Publish changes (${waiting.count})`}
        </Button>
      </div>
      {!nothingWaiting && (
        <button
          type="button"
          tw="text-xs text-gray-500 font-medium mt-2 underline"
          data-testid="discard-changes"
          disabled={busy}
          onClick={() => setStep('discard')}
        >
          Discard changes
        </button>
      )}

      {/*
        ONE pop-up whose content follows the step, not one pop-up per step:
        with two, "Yes" closed one while the next opened, and in Safari's
        engine the second could stay on screen for good, over the page, after
        the publish had gone through (run of 7 Oct 2026).
      */}
      <Modal show={step !== 'idle'} onClose={close}>
        {shown === 'ask' && (
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="show-what-will-change"
          >
            <Title3>Show me what will change?</Title3>
            {problem && (
              <p tw="text-sm text-red-600" data-testid="publish-problem">
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="publish-now"
                disabled={busy}
                onClick={publish}
              >
                No, publish now
              </Button>
              <Button testId="show-the-list" onClick={() => setStep('list')}>
                Yes
              </Button>
            </div>
          </div>
        )}
        {shown === 'list' && (
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="what-will-change"
          >
            <Title3>What will change</Title3>
            {byScreen.map(([screen, changes]) => (
              <section key={screen} tw="flex flex-col gap-2">
                <h4 tw="text-sm font-semibold text-gray-900">{screen}</h4>
                <ul tw="flex flex-col gap-2">
                  {changes.map((change) => (
                    <li
                      key={`${screen}-${change.thing}`}
                      tw="text-sm text-gray-700"
                      data-testid="waiting-change"
                    >
                      <span tw="font-medium">{change.thing}</span>
                      {change.before !== undefined &&
                        change.after !== undefined && (
                          <>
                            <span tw="block text-gray-400 line-through">
                              {change.before}
                            </span>
                            <span tw="block">{change.after}</span>
                          </>
                        )}
                      {waiting.last_change_by && (
                        <span tw="block text-xs text-gray-400">
                          {`${waiting.last_change_by}, ${hourAndMinute(
                            waiting.last_change_at
                          )}`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            {waiting.keeps_note && (
              <p tw="text-sm text-gray-500" data-testid="keeps-note">
                Running LinkedIn and Google ads keep their current text and
                locations.
              </p>
            )}
            {problem && (
              <p tw="text-sm text-red-600" data-testid="publish-problem">
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="list-discard"
                disabled={busy}
                onClick={() => setStep('discard')}
              >
                Discard changes
              </Button>
              <Button variant="secondary" testId="list-not-now" onClick={close}>
                Not now
              </Button>
              <Button testId="list-publish" disabled={busy} onClick={publish}>
                Publish
              </Button>
            </div>
          </div>
        )}
        {shown === 'discard' && (
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="discard-question"
          >
            <Title3>Discard changes?</Title3>
            <p tw="text-sm text-gray-700">
              This returns to what is public now. It cannot be undone.
            </p>
            {problem && (
              <p tw="text-sm text-red-600" data-testid="publish-problem">
                {problem}
              </p>
            )}
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="discard-cancel"
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                testId="discard-confirm"
                disabled={busy}
                onClick={discard}
              >
                Discard
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PublishChanges;
